import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import usePageMeta from '../../utilities/usePageMeta';
import { proseStyles } from './ArticlePage';
import heroBanner from '../../resources/css/images/college-banner.jpg';

/** The three things people come to the site to do. */
const QUICK_ACTIONS = [
    { title: 'Reserve Online', description: 'Book your unit with us online.', href: '/available-units' },
    { title: 'Pay Bill', description: 'Pay your bill with a credit card online.', href: '/user-storage-units' },
    { title: 'Contact Us', description: 'We’re here to help, so contact us today!', href: '/contact' },
];

export default function Index() {
    const navigate = useNavigate();

    usePageMeta({
        title: 'Climate Controlled Self-Storage in East Troy, WI',
        description: 'Climate controlled self-storage units in East Troy, WI. 24/7 access, drive-up ground level units, video surveillance and month to month leasing. Reserve online.',
        path: '/',
    });

    return (
        <Box>
            {/* The template drew this with a background-image rule and an absolutely positioned bar. */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: '18em', sm: '20em', md: '24em', lg: '28em' },
                    backgroundImage: `url(${heroBanner})`,
                    backgroundPosition: 'center center',
                    backgroundSize: 'cover',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        backgroundColor: 'rgba(27, 27, 27, 0.75)',
                        color: '#fff',
                        padding: { xs: '20px', md: '1.5em 0' },
                        textAlign: 'center',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1em',
                    }}
                >
                    <Typography variant={'h6'} component={'p'} sx={{ margin: 0, opacity: 0.85, fontWeight: 400 }}>
                        Book your unit today
                    </Typography>
                    <Button variant={'contained'} onClick={() => navigate('/available-units')}>
                        Book Now
                    </Button>
                </Box>
            </Box>

            <Container maxWidth={'lg'} sx={{ paddingY: { xs: '32px', md: '56px' } }}>
                <Grid container spacing={3}>
                    {QUICK_ACTIONS.map((action) => (
                        <Grid item xs={12} md={4} key={action.href}>
                            <Paper
                                elevation={0}
                                onClick={() => navigate(action.href)}
                                sx={{
                                    height: '100%',
                                    padding: '28px 24px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: 2,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <Typography
                                    variant={'h6'}
                                    component={'h2'}
                                    sx={{ fontWeight: 700, color: 'primary.main', marginBottom: '8px' }}
                                >
                                    {action.title}
                                </Typography>
                                <Typography variant={'body1'} sx={{ color: 'text.secondary' }}>
                                    {action.description}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            <Container maxWidth={'lg'} sx={{ paddingBottom: { xs: '32px', md: '64px' } }}>
                <Typography
                    variant={'h3'}
                    component={'h1'}
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        letterSpacing: '-0.025em',
                        marginBottom: { xs: '20px', md: '28px' },
                        fontSize: { xs: '1.9rem', sm: '2.2rem', md: '2.6rem' },
                    }}
                >
                    East Troy, WI Self-Storage
                </Typography>

                <Box component={'article'} sx={proseStyles}>
                    <p>Welcome to Executive Storage Suites, the number one location for all your East Troy, WI storage needs! Serving the area since 2016, our East Troy storage facility at 2481 Executive Drive has a wide range of climate controlled units to choose from. Whether you are in need of personal storage, business storage, long or short term, we have the right solutions to accommodate all types of belongings. We are conveniently located just blocks east of highway 120 on Honeycreek Rd and Executive Drive.</p>

                    <h3>Storage Rentals</h3>

                    <p>If you are coming from the surrounding areas in Walworth County such as Mukwonago, Lake Geneva, Burlington or Elkhorn, we are easy to find and easy to get to. Our property is located in the East Troy Business Park, just blocks from the Aurora Health Center. With month to month leasing plans and affordable storage unit prices, our facility is number one for storage in the 53120 zip code.</p>

                    <h3>Easy Access &amp; Enhanced Security</h3>

                    <p>All units are conveniently located on the ground level, with easy drive-up access for unloading and moving in. Climate control offers dependable protection of your belongings against extreme temperatures and humidity. When you rent East Troy storage with Executive Storage Suites, you can count on security and safekeeping for all your storage items. Our property features recorded video surveillance, via 14 strategically positioned cameras and motion-activated lights.</p>

                    <h3>24/7 Self-Storage</h3>

                    <p>One feature that our tenants enjoy the most is 24/7 access to all units. Instead of finding time in your busy schedule to meet office hours, we allow you the freedom to store on your own time, day or night. In addition, our 24/7 kiosk lets you arrange rentals and move-ins whenever you want. Self storage can be a hassle, but it doesn’t have to be when you store with Executive Storage Suites. We look forward to storing with you!</p>
                </Box>
            </Container>

            {/* The template's #cta band, in the brand green. */}
            <Box sx={{ backgroundColor: 'primary.main', color: '#fff', paddingY: { xs: '32px', md: '56px' } }}>
                <Container maxWidth={'lg'}>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1.2em',
                        textAlign: 'center',
                    }}>
                        <Typography variant={'h5'} component={'h2'} sx={{ margin: 0, fontWeight: 600 }}>
                            Are you ready?
                        </Typography>
                        <Button
                            variant={'contained'}
                            onClick={() => navigate('/available-units')}
                            sx={{
                                backgroundColor: '#fff',
                                color: 'primary.main',
                                '&:hover': { backgroundColor: '#f1f1f1' },
                            }}
                        >
                            Book Now
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}
